/**
 * Admin & Tools Help Content
 *
 * Comprehensive help for admin pages: user management, policies, settings,
 * leave configuration, and domain verification.
 */

import type { HelpContent } from '../types';
import {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createWalkthrough,
  createTroubleshooting,
  createRelatedLink,
  createFieldHelp,
} from '../utils';

// =============================================================================
// MAIN ADMIN HELP
// =============================================================================

export const adminHelp: HelpContent = createHelpContent({
  id: 'admin',
  title: 'Admin & Tools',
  description: 'System administration, user management, and company settings',
  lastUpdated: '2026-02-08',
  version: '1.0',

  tips: [
    createTip(
      'tip-caution',
      'warning',
      'Admin Actions',
      'Changes made in admin settings affect the entire organization. Double-check before saving, especially for user roles and leave configurations.'
    ),
    createTip(
      'tip-audit',
      'pro-tip',
      'Check Before Changes',
      'Before making bulk changes, review the affected users or policies. Consider testing with a single record first.'
    ),
  ],

  sections: [
    createSection(
      'overview',
      'Overview',
      `<p>The Admin section provides tools for managing your organization's settings, users, and policies. As an administrator, you can:</p>
      <ul>
        <li><strong>User Management</strong> — Add, edit, and deactivate user accounts</li>
        <li><strong>Company Policies</strong> — Create and publish organizational policies</li>
        <li><strong>System Settings</strong> — Configure application-wide settings</li>
        <li><strong>Leave Management</strong> — Set up leave types, holiday calendars, and public holidays</li>
        <li><strong>Verified Domains</strong> — Manage domain verification for your organization</li>
      </ul>`
    ),
    createSection(
      'user-management',
      'User Management',
      `<p>Manage user accounts for your organization:</p>
      <ul>
        <li><strong>Add Users</strong> — Create new user accounts with role, department, and team assignments</li>
        <li><strong>Edit Users</strong> — Update user details, change roles, or reassign departments</li>
        <li><strong>Deactivate Users</strong> — Disable accounts for departing employees (preserves data)</li>
        <li><strong>Role Assignment</strong> — Set user roles: Admin, Manager, Team Lead, or Staff</li>
      </ul>
      <p>User roles determine access levels throughout the application.</p>`
    ),
    createSection(
      'policies',
      'Company Policies',
      `<p>Create and manage organizational policies:</p>
      <ul>
        <li>Create new policies with rich text content</li>
        <li>Assign policies to specific departments or make them company-wide</li>
        <li>Set policy status (draft, published, archived)</li>
        <li>Attach supporting documents</li>
        <li>Control team visibility</li>
      </ul>
      <p>Published policies appear in the Knowledge Base for employees to access.</p>`
    ),
    createSection(
      'settings',
      'System Settings',
      `<p>Configure application-wide settings including:</p>
      <ul>
        <li>Notification preferences (email templates, triggers)</li>
        <li>Security settings (password policies, session timeouts)</li>
        <li>Display options (date format, timezone defaults)</li>
        <li>Integration settings</li>
      </ul>`
    ),
    createSection(
      'leave-config',
      'Leave Configuration',
      `<p>Set up and manage the leave system:</p>
      <ul>
        <li><strong>Leave Types</strong> — Create types like Vacation, Sick, Personal with quotas and approval rules</li>
        <li><strong>Holiday Calendars</strong> — Define regional holiday calendars</li>
        <li><strong>Public Holidays</strong> — Add individual public holidays to calendars</li>
        <li><strong>Import Holidays</strong> — Bulk import holidays for a region/year</li>
      </ul>`
    ),
    createSection(
      'domains',
      'Verified Domains',
      '<p>Domain verification confirms ownership of your organization\'s email domains. Verified domains enable automatic user authentication and ensure only authorized email addresses can be used for accounts.</p>'
    ),
  ],

  walkthroughs: [
    createWalkthrough(
      'walkthrough-add-user',
      'How to Add a New User',
      'Create a new user account step by step.',
      [
        {
          title: 'Go to User Management',
          description: 'Navigate to Admin > Users or click the "Add User" button.',
        },
        {
          title: 'Enter basic details',
          description: 'Fill in name, email address, and username.',
        },
        {
          title: 'Set the role',
          description: 'Choose the appropriate role: Admin, Manager, Team Lead, or Staff. This determines their access level.',
        },
        {
          title: 'Assign department and team',
          description: 'Select the department and optionally assign to a team.',
        },
        {
          title: 'Configure additional settings',
          description: 'Set holiday calendar, reporting manager, and monthly cost if applicable.',
        },
        {
          title: 'Save and invite',
          description: 'Save the user. They will receive an invitation email to set their password and log in.',
        },
      ],
      3
    ),
    createWalkthrough(
      'walkthrough-policy',
      'How to Create a Company Policy',
      'Create and publish a new organizational policy.',
      [
        {
          title: 'Go to Policies',
          description: 'Navigate to Admin > Policies.',
        },
        {
          title: 'Click "Add Policy"',
          description: 'Open the policy creation form.',
        },
        {
          title: 'Enter policy details',
          description: 'Set the title, select the department (or make it company-wide), and write the policy content.',
        },
        {
          title: 'Set visibility and status',
          description: 'Choose team visibility and set status to "Published" when ready (use "Draft" while still editing).',
        },
      ],
      2
    ),
    createWalkthrough(
      'walkthrough-leave-types',
      'How to Set Up Leave Types',
      'Configure leave types for your organization.',
      [
        {
          title: 'Go to Leave Management',
          description: 'Navigate to Admin > Leave Management > Leave Types.',
        },
        {
          title: 'Add a new leave type',
          description: 'Click "Add Leave Type" and enter the name (e.g., Vacation, Sick, Personal).',
        },
        {
          title: 'Set the quota',
          description: 'Define the default annual quota (number of days).',
        },
        {
          title: 'Configure approval rules',
          description: 'Set whether this type requires manager approval and any capacity impact rules.',
        },
      ],
      3
    ),
  ],

  troubleshooting: [
    createTroubleshooting(
      'trouble-login',
      'New user cannot log in after creation',
      [
        'Invitation email went to spam',
        'User hasn\'t set their password yet',
        'Account may be deactivated',
        'Email domain not verified',
      ],
      [
        {
          description: 'Check invitation email',
          steps: [
            'Ask the user to check their spam/junk folder',
            'Verify the email address was entered correctly',
            'Resend the invitation from the user management page',
          ],
        },
        {
          description: 'Verify account status',
          steps: [
            'Check that the account is active (not deactivated)',
            'Ensure the email domain is verified for your organization',
          ],
        },
      ]
    ),
    createTroubleshooting(
      'trouble-roles',
      'User has wrong permissions after role change',
      [
        'Browser cache showing old permissions',
        'Role change not saved properly',
        'User needs to log out and back in',
      ],
      [
        {
          description: 'Clear and refresh',
          steps: [
            'Ask the user to log out and log back in',
            'Clear browser cache if issues persist',
            'Verify the role was saved correctly in admin settings',
          ],
        },
      ]
    ),
    createTroubleshooting(
      'trouble-leave-balance',
      'Leave balances not updating after configuration change',
      [
        'Changes may take time to propagate',
        'Existing leave requests may need recalculation',
      ],
      [
        {
          description: 'Allow propagation time',
          steps: [
            'Wait a few minutes for changes to take effect',
            'Ask affected users to refresh their leave page',
            'Check if existing requests need manual adjustment',
          ],
        },
      ]
    ),
  ],

  faqs: [
    createFAQ(
      'faq-roles',
      'What are the different user roles?',
      'Admin has full system access. Manager can manage teams and view analytics. Team Lead manages their team and approvals. Staff is the default role with personal features access.',
      ['roles', 'admin', 'manager', 'team lead', 'staff', 'permissions']
    ),
    createFAQ(
      'faq-deactivate',
      'What\'s the difference between deactivating and deleting a user?',
      'Deactivating disables the account but preserves all data (timesheets, tasks, etc.). Deleting permanently removes the user. Always prefer deactivation for departing employees.',
      ['deactivate', 'delete', 'remove', 'disable', 'departing']
    ),
    createFAQ(
      'faq-bulk',
      'Can I add multiple users at once?',
      'Currently, users need to be added individually through the admin interface. For large onboarding batches, contact the development team about bulk import options.',
      ['bulk', 'import', 'multiple', 'batch', 'many']
    ),
    createFAQ(
      'faq-policy-visibility',
      'How do I control who sees a policy?',
      'When creating a policy, set the Department field to target a specific department, or leave it as "All" for company-wide visibility. The team visibility setting provides additional control.',
      ['policy', 'visibility', 'department', 'who sees']
    ),
    createFAQ(
      'faq-leave-setup',
      'How do I set up leave types for different regions?',
      'Create separate holiday calendars for each region and assign them to users based on their location. Leave types themselves are global, but quotas can be adjusted per user if needed.',
      ['leave', 'region', 'calendar', 'setup', 'location']
    ),
    createFAQ(
      'faq-holiday-calendar',
      'How do holiday calendars work?',
      'Holiday calendars define the public holidays for a specific region or country. Each user is assigned a holiday calendar which determines their non-working days. Multiple calendars can coexist for a multi-region organization.',
      ['holiday', 'calendar', 'public', 'region']
    ),
    createFAQ(
      'faq-audit',
      'Is there an audit trail for admin actions?',
      'Key admin actions like user creation, role changes, and policy updates are logged. Contact your system administrator for access to audit logs.',
      ['audit', 'trail', 'log', 'history', 'track']
    ),
    createFAQ(
      'faq-password',
      'How do I reset a user\'s password?',
      'Users can reset their own password via the login page. As an admin, you can trigger a password reset email from the user management page.',
      ['password', 'reset', 'forgot', 'login']
    ),
  ],

  relatedLinks: [
    createRelatedLink('Directory', '/directory', { description: 'View the employee directory' }),
    createRelatedLink('Leave Management', '/leave', { description: 'User-facing leave management' }),
    createRelatedLink('Analytics', '/analytics', { description: 'Organization-wide analytics' }),
    createRelatedLink('Knowledge Base', '/knowledge', { description: 'Published policies appear here' }),
  ],

  supportContact: {
    name: 'IT Administration',
    email: 'admin@company.com',
    slackChannel: '#help-admin',
  },

  keywords: [
    'admin',
    'settings',
    'users',
    'policies',
    'configuration',
    'management',
    'roles',
    'permissions',
    'leave types',
    'holiday calendar',
  ],

  visibility: { visibleTo: ['admin'] },
});

// =============================================================================
// SECTION-LEVEL HELP
// =============================================================================

export const adminUserManagementHelp: HelpContent = createHelpContent({
  id: 'admin:users',
  title: 'User Management',
  description: 'Add, edit, and manage user accounts',
  parentId: 'admin',
  sections: [
    createSection('overview', 'Overview', '<p>Manage all user accounts in your organization. Add new users, edit existing profiles, change roles, and deactivate accounts for departing employees.</p>'),
    createSection('adding', 'Adding Users', '<p>Create new accounts by filling in user details: name, email, role, department, holiday calendar, and optional fields like monthly cost and reporting manager.</p>'),
    createSection('editing', 'Editing Users', '<p>Update any user\'s details including their role, department, team, and manager. Changes take effect immediately after saving.</p>'),
  ],
  faqs: [
    createFAQ('faq-1', 'Can I change a user\'s email address?', 'Yes, but ensure the new email domain is verified for your organization. The user will need to use the new email to log in.', ['email', 'change', 'update']),
    createFAQ('faq-2', 'What happens to a deactivated user\'s data?', 'All data (timesheets, tasks, projects) is preserved. The user simply cannot log in. Reactivation is possible at any time.', ['deactivate', 'data', 'preserve']),
  ],
  keywords: ['users', 'accounts', 'add', 'edit', 'deactivate', 'roles'],
  visibility: { visibleTo: ['admin'] },
});

export const adminPoliciesHelp: HelpContent = createHelpContent({
  id: 'admin:policies',
  title: 'Company Policies',
  description: 'Create and manage organizational policies',
  parentId: 'admin',
  sections: [
    createSection('overview', 'Overview', '<p>Create, edit, and publish company policies. Policies can target specific departments or be company-wide. Published policies are accessible to employees through the Knowledge Base.</p>'),
    createSection('status', 'Policy Status', '<p>Policies have three statuses: <strong>Draft</strong> (not visible to employees), <strong>Published</strong> (visible and active), and <strong>Archived</strong> (hidden but preserved for records).</p>'),
  ],
  faqs: [
    createFAQ('faq-1', 'Can employees comment on policies?', 'This depends on your configuration. Policies in the Knowledge Base may support feedback mechanisms.', ['comment', 'feedback', 'employee']),
  ],
  keywords: ['policies', 'company', 'publish', 'draft', 'archive'],
  visibility: { visibleTo: ['admin'] },
});

export const adminSettingsHelp: HelpContent = createHelpContent({
  id: 'admin:settings',
  title: 'System Settings',
  description: 'Configure application-wide settings and preferences',
  parentId: 'admin',
  sections: [
    createSection('overview', 'Overview', '<p>System settings control application-wide behavior including notifications, security policies, display preferences, and integrations. Changes here affect all users.</p>'),
  ],
  faqs: [
    createFAQ('faq-1', 'Will settings changes affect existing users immediately?', 'Most settings take effect immediately. Some (like security policies) may require users to log out and back in.', ['immediate', 'effect', 'when']),
  ],
  keywords: ['settings', 'configuration', 'system', 'preferences'],
  visibility: { visibleTo: ['admin'] },
});

export const adminLeaveManagementHelp: HelpContent = createHelpContent({
  id: 'admin:leave',
  title: 'Leave Configuration',
  description: 'Set up leave types, holiday calendars, and public holidays',
  parentId: 'admin',
  sections: [
    createSection('overview', 'Overview', '<p>Configure the leave management system for your organization. Set up leave types with quotas, create regional holiday calendars, and manage public holidays.</p>'),
    createSection('leave-types', 'Leave Types', '<p>Define the types of leave available (Vacation, Sick, Personal, etc.) with default quotas, approval requirements, and capacity impact settings.</p>'),
    createSection('calendars', 'Holiday Calendars', '<p>Create holiday calendars for different regions or offices. Each calendar contains the public holidays applicable to that location. Assign calendars to users based on their work location.</p>'),
    createSection('holidays', 'Public Holidays', '<p>Add individual public holidays to calendars. Specify the date, name, and whether it\'s a full or half day. You can also bulk import holidays for a year.</p>'),
  ],
  faqs: [
    createFAQ('faq-1', 'Can different users have different leave quotas?', 'Yes, while leave types have default quotas, individual user quotas can be adjusted from their profile in user management.', ['quota', 'individual', 'different']),
    createFAQ('faq-2', 'How do I import holidays for a new year?', 'Go to Import Holidays, select the calendar and year, and import from a predefined list or upload a CSV file.', ['import', 'bulk', 'new year']),
  ],
  keywords: ['leave types', 'holiday calendar', 'public holidays', 'quotas', 'configuration'],
  visibility: { visibleTo: ['admin'] },
});

export const adminVerifiedDomainsHelp: HelpContent = createHelpContent({
  id: 'admin:domains',
  title: 'Verified Domains',
  description: 'Domain verification for your organization',
  parentId: 'admin',
  sections: [
    createSection('overview', 'Overview', '<p>Domain verification confirms ownership of your organization\'s email domains. This enables automatic user authentication and ensures only authorized email addresses can create accounts.</p>'),
  ],
  faqs: [
    createFAQ('faq-1', 'How do I verify a domain?', 'Add your domain and follow the DNS verification steps. You\'ll need access to your domain\'s DNS settings to add a verification record.', ['verify', 'DNS', 'how']),
  ],
  keywords: ['domain', 'verification', 'DNS', 'email', 'authentication'],
  visibility: { visibleTo: ['admin'] },
});

// =============================================================================
// FIELD-LEVEL HELP
// =============================================================================

export const adminFieldHelp = {
  userRole: createFieldHelp(
    'admin:users:role',
    'User Role',
    'Determines the user\'s access level. Admin: full system access. Manager: team management + analytics. Team Lead: team-level management. Staff: standard access.',
    { example: 'Staff' }
  ),
  userDepartment: createFieldHelp(
    'admin:users:department',
    'Department',
    'The organizational department this user belongs to. Affects which teams, policies, and analytics are visible to them.'
  ),
  userHolidayCalendar: createFieldHelp(
    'admin:users:holiday-calendar',
    'Holiday Calendar',
    'Determines which public holidays apply to this user. Choose the calendar matching their work location or region.'
  ),
  userMonthlyCost: createFieldHelp(
    'admin:users:monthly-cost',
    'Monthly Cost',
    'The user\'s monthly cost rate used for project cost analysis and budgeting reports. This is typically the loaded cost (salary + benefits + overhead).',
    { example: '5000' }
  ),
  userReportingManager: createFieldHelp(
    'admin:users:reporting-manager',
    'Reporting Manager',
    'The user\'s direct manager who will approve their leave requests, review timesheets, and see them in team analytics.'
  ),
};
